import React, { useEffect, useState } from 'react';
import permissionService, { Permission, Role } from '../services/permissionService';

interface PermissionGuardProps {
  children: React.ReactNode;
  permission?: Permission | string;
  permissions?: (Permission | string)[];
  role?: Role | string;
  roles?: (Role | string)[];
  pbsRole?: string;
  pbsRoles?: string[];
  requireAll?: boolean; // For permissions array: require all (true) or any (false, default)
  fallback?: React.ReactNode;
  hideIfNoPermission?: boolean; // If true, returns null instead of fallback
}

/**
 * PermissionGuard Component
 * 
 * Conditionally renders children based on user permissions or roles
 * 
 * Usage examples:
 * 
 * // Single permission
 * <PermissionGuard permission={Permission.ENTER_REVENUE}>
 *   <button>Add Revenue</button>
 * </PermissionGuard>
 * 
 * // Multiple permissions (any)
 * <PermissionGuard permissions={[Permission.VIEW_COSTS, Permission.VIEW_ALL_COSTS]}>
 *   <CostsSummary />
 * </PermissionGuard>
 * 
 * // Multiple permissions (all required)
 * <PermissionGuard permissions={[Permission.ENTER_REVENUE, Permission.MODIFY_REVENUE]} requireAll>
 *   <RevenueEditor />
 * </PermissionGuard>
 * 
 * // Role-based
 * <PermissionGuard role={Role.BU_HEAD}>
 *   <ProjectHealthDashboard />
 * </PermissionGuard>
 * 
 * // Multiple roles
 * <PermissionGuard roles={[Role.BU_HEAD, Role.OPERATIONS]}>
 *   <AdminPanel />
 * </PermissionGuard>
 * 
 * // With fallback
 * <PermissionGuard permission={Permission.VIEW_REVENUE} fallback={<div>Access Denied</div>}>
 *   <RevenueChart />
 * </PermissionGuard>
 * 
 * // Hide completely if no permission
 * <PermissionGuard permission={Permission.DELETE_MEMBERS} hideIfNoPermission>
 *   <button>Delete Member</button>
 * </PermissionGuard>
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  permission,
  permissions,
  role,
  roles,
  pbsRole,
  pbsRoles,
  requireAll = false,
  fallback = null,
  hideIfNoPermission = false,
}) => {
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkPermissions = async () => {
      setIsLoading(true);
      
      try {
        let access = true;

        // Check single permission
        if (permission) {
          access = permissionService.hasPermission(permission);
        }

        // Check multiple permissions
        if (permissions && permissions.length > 0) {
          if (requireAll) {
            access = permissionService.hasAllPermissions(permissions);
          } else {
            access = permissionService.hasAnyPermission(permissions);
          }
        }

        // Check single role
        if (role) {
          access = access && permissionService.hasRole(role);
        }

        // Check multiple roles
        if (roles && roles.length > 0) {
          access = access && permissionService.hasAnyRole(roles);
        }

        if (pbsRole) {
          access = access && permissionService.hasPbsRole(pbsRole);
        }

        if (pbsRoles && pbsRoles.length > 0) {
          access = access && permissionService.hasAnyPbsRole(pbsRoles);
        }

        setHasAccess(access);
      } catch (error) {
        console.error('Permission check failed:', error);
        setHasAccess(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkPermissions();
  }, [permission, permissions, role, roles, pbsRole, pbsRoles, requireAll]);

  // Show loading state (optional)
  if (isLoading) {
    return null; // or a loading spinner if desired
  }

  // If user doesn't have access
  if (!hasAccess) {
    if (hideIfNoPermission) {
      return null;
    }
    return <>{fallback}</>;
  }

  // User has access, render children
  return <>{children}</>;
};

export default PermissionGuard;

/**
 * Hook for permission checks in functional components
 * 
 * Usage:
 * const { hasPermission, hasRole, canModify, canDelete } = usePermissions();
 * 
 * if (hasPermission(Permission.ENTER_REVENUE)) {
 *   // Show revenue form
 * }
 */
export const usePermissions = () => {
  const [, forceUpdate] = useState({});

  useEffect(() => {
    // Load permissions when hook is first used
    permissionService.initializePermissions().then(() => {
      forceUpdate({});
    });
  }, []);

  return {
    hasPermission: (permission: Permission | string) => permissionService.hasPermission(permission),
    hasAnyPermission: (permissions: (Permission | string)[]) => permissionService.hasAnyPermission(permissions),
    hasAllPermissions: (permissions: (Permission | string)[]) => permissionService.hasAllPermissions(permissions),
    hasRole: (role: Role | string) => permissionService.hasRole(role),
    hasAnyRole: (roles: (Role | string)[]) => permissionService.hasAnyRole(roles),
    hasPbsRole: (role: string) => permissionService.hasPbsRole(role),
    hasAnyPbsRole: (roles: string[]) => permissionService.hasAnyPbsRole(roles),
    getPbsRoles: () => permissionService.getPbsRoles(),
    getRole: () => permissionService.getRole(),
    getRoleDisplayName: () => permissionService.getRoleDisplayName(),
    canModifyAllocation: (date: string) => permissionService.canModifyAllocation(date),
    canDeleteAllocation: (date: string) => permissionService.canDeleteAllocation(date),
    isFutureDate: (date: string) => permissionService.isFutureDate(date),
  };
};

/**
 * Role Badge Component
 * Displays user's role as a badge
 */
export const RoleBadge: React.FC<{ className?: string }> = ({ className = '' }) => {
  const role = permissionService.getRole();
  const displayName = permissionService.getRoleDisplayName();

  const roleColors: Record<string, string> = {
    [Role.PROJECT_OWNER]: 'bg-blue-100 text-blue-800',
    [Role.BU_HEAD]: 'bg-purple-100 text-purple-800',
    [Role.OPERATIONS]: 'bg-green-100 text-green-800',
    [Role.ACCOUNTS]: 'bg-yellow-100 text-yellow-800',
  };

  const colorClass = roleColors[role] || 'bg-gray-100 text-gray-800';

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass} ${className}`}>
      {displayName}
    </span>
  );
};


